/**
 * Slip Verification History API Client
 * API functions for querying slip verification history from slip_transactions table
 */

import axiosInstance from '@/lib/api/axios-instance';
import { getBusinessId } from '@/lib/api/axios-instance';

// ===========================
// Type Definitions
// ===========================

export interface OriginalTransactionData {
  id: string;
  transaction_ref: string | null;
  transaction_date: string | null;
  transaction_time: string | null;
  amount: string | number | null;
  sender_account_number: string | null;
  sender_account_name: string | null;
  sender_bank_code: string | null;
  receiver_account_number: string | null;
  receiver_account_name: string | null;
  verification_status: string | null;
  channel: string | null;
  created_at: string;
  branch_name: string | null;
}

export interface SlipVerification {
  id: string;
  operation_id: string;
  transaction_ref: string;
  transaction_date: string;
  transaction_time: string | null;
  amount: string | number;
  currency: string;
  bank_code: string;
  sender_account_number: string | null;
  sender_account_name: string | null;
  sender_bank_code: string | null;
  receiver_account_number: string | null;
  receiver_account_name: string | null;
  receiver_bank_code: string | null;
  verification_status: 'verified' | 'failed' | 'duplicate' | 'pending';
  verification_method: 'API_V2' | 'API_V3' | 'LINE_CONNECT' | null;
  is_duplicate: boolean;
  duplicate_of_id: string | null;
  transaction_type: string | null;
  classification_confidence: number | null;
  channel: 'API' | 'LINE' | 'MANUAL';
  credits_used: number;
  credits_saved: number;
  api_cost: string | number;
  processing_time_ms: number | null;
  branch_id: string | null;
  account_id: string | null;
  validation_result: string | null;
  validation_error_type: string | null;
  slip_quality_status: string | null;
  account_match_status: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  branch_name?: string;
  branch_code?: string;
  account_display_name?: string;
  account_bank_code?: string;
  account_number_display?: string;
  business_name?: string;
  // Original transaction (populated for duplicate slips)
  original_transaction?: OriginalTransactionData | null;
}

export interface SlipVerificationListResponse {
  success: boolean;
  data: {
    verifications: SlipVerification[];
    pagination: {
      page: number;
      limit: number;
      total_records: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
    filters_applied?: {
      date_from: string | null;
      date_to: string | null;
      channel: string | null;
      verification_method: string | null;
      status: string | null;
      branch_id: string | null;
      account_id: string | null;
      search: string | null;
    };
    sorting?: {
      field: string;
      order: string;
    };
    summary?: {
      total_amount: number;
      total_credits_used: number;
      total_credits_saved: number;
      verified_count: number;
      failed_count: number;
      duplicate_count: number;
    };
  };
  metadata?: {
    processing_time_ms: number;
    timestamp: string;
  };
}

export interface SlipVerificationDetailResponse {
  success: boolean;
  data: {
    verification: SlipVerification;
    original_transaction?: SlipVerification | null;
  };
}

// Filter Types
export interface SlipVerificationFilters {
  page?: number;
  limit?: number;
  channel?: 'API' | 'LINE' | 'MANUAL' | 'all';
  verification_method?: 'API_V2' | 'API_V3' | 'LINE_CONNECT' | 'all';
  status?: 'verified' | 'failed' | 'duplicate' | 'pending' | 'all';
  startDate?: string;
  endDate?: string;
  search?: string;
  branch_id?: string;
  account_id?: string;
}

// ===========================
// API Functions
// ===========================

export const slipVerificationAPI = {
  /**
   * Get list of slip verifications with filters and pagination
   */
  async getSlipVerifications(filters: SlipVerificationFilters = {}): Promise<SlipVerificationListResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.verification_method && filters.verification_method !== 'all') params.append('verification_method', filters.verification_method);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.branch_id) params.append('branch_id', filters.branch_id);
    if (filters.account_id) params.append('account_id', filters.account_id);

    const response = await axiosInstance.get<SlipVerificationListResponse>(
      `/api/v2/business/${businessId}/slip-verification-history?${params.toString()}`
    );

    return response.data;
  },

  /**
   * Get detailed information about a specific slip verification
   */
  async getSlipVerificationDetail(id: string): Promise<SlipVerificationDetailResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const response = await axiosInstance.get<SlipVerificationDetailResponse>(
      `/api/v2/business/${businessId}/slip-verification-history/${id}`
    );

    return response.data;
  },

  /**
   * Export slip verifications to Excel (using same endpoint as transactions for now)
   * TODO: Create dedicated export endpoint if needed
   */
  async exportSlipVerifications(
    format: 'excel' | 'csv',
    filters: SlipVerificationFilters = {}
  ): Promise<Blob> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);
    if (filters.search) params.append('search', filters.search);

    const response = await axiosInstance.get(
      `/api/v2/business/${businessId}/slip-verification-history/export?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  },
};

export default slipVerificationAPI;
